import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Package, RefreshCw, Search, AlertCircle, TrendingDown, Loader2, Boxes,
  ArrowDown, ArrowUp, Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import BulkActionsBar from "@/components/bulk/BulkActionsBar";
import PageHeader from "@/components/PageHeader";
import CommonFilterBar from "@/components/reports/CommonFilterBar";


const round2 = (n) => Math.round(n * 100) / 100;

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;
  const [status, setStatus] = useState("all"); // all|active|inactive
  const [filters, setFilters] = useState({ zone_id: "", sub_zone_id: "", distributor_id: "", sku_id: "" });
  const [movementsFor, setMovementsFor] = useState(null); // {distributor_id, sku_id, sku_code}
  const [movements, setMovements] = useState([]);
  const [editRow, setEditRow] = useState(null); // the stock row being edited
  const [editQty, setEditQty] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkToggling, setBulkToggling] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const commonParams = {
        q: q || undefined,
        status: status === "all" ? undefined : status,
        zone_id: filters.zone_id || undefined,
        sub_zone_id: filters.sub_zone_id || undefined,
        distributor_id: filters.distributor_id || undefined,
        sku_id: filters.sku_id || undefined,
      };
      const [inv, sum] = await Promise.all([
        api.get("/inventory", { params: { page, page_size: PAGE_SIZE, ...commonParams } }),
        api.get("/inventory/summary", { params: {
          zone_id: commonParams.zone_id, sub_zone_id: commonParams.sub_zone_id,
          distributor_id: commonParams.distributor_id, sku_id: commonParams.sku_id,
        } }),
      ]);
      setItems(inv.data.items || []);
      setTotal(inv.data.total || 0);
      setSummary(sum.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load inventory");
    } finally { setBusy(false); }
  }, [q, status, filters, page]);

  useEffect(() => { load(); }, [load]);

  // A new search/filter starts again from the first page.
  useEffect(() => { setPage(1); }, [q, status, filters]);

  // Selection is by visible row — clear it whenever the filtered set
  // changes underneath the checkboxes (a stale selection acting on rows
  // the admin can no longer see would be surprising).
  useEffect(() => { setSelectedIds(new Set()); }, [items]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = items.length > 0 && items.every((r) => selectedIds.has(r.id));
  const toggleSelectAll = () => {
    setSelectedIds(allVisibleSelected ? new Set() : new Set(items.map((r) => r.id)));
  };

  const bulkToggle = async (is_active) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkToggling(true);
    try {
      const { data } = await api.patch("/inventory/bulk-toggle", { ids, is_active });
      toast.success(`${data.updated} row${data.updated === 1 ? "" : "s"} ${is_active ? "activated" : "deactivated"}`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk toggle failed"); }
    finally { setBulkToggling(false); }
  };

  const toggleActive = useCallback(async (row) => {
    try {
      await api.patch(`/inventory/${row.id}/toggle`, { is_active: !row.is_active });
      toast.success(`${row.sku_code} · ${row.is_active ? "deactivated" : "activated"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  }, [load]);

  const openMovements = useCallback(async (row) => {
    setMovementsFor(row);
    try {
      const { data } = await api.get("/inventory/movements", {
        params: { distributor_id: row.distributor_id, sku_id: row.sku_id,
                  page: 1, page_size: 40 },
      });
      setMovements(data.items || []);
    } catch (e) { setMovements([]); }
  }, []);

  const openEdit = (row) => {
    setEditRow(row);
    setEditQty(String(row.on_hand_kg ?? 0));
    setEditNotes("");
  };

  const saveEdit = async () => {
    const newKg = Number(editQty);
    if (isNaN(newKg) || newKg < 0) { toast.error("Enter a valid quantity"); return; }
    const deltaKg = round2(newKg - (editRow.on_hand_kg ?? 0));
    if (deltaKg === 0) { toast.error("Quantity unchanged"); return; }
    if (!editNotes.trim() || editNotes.trim().length < 3) {
      toast.error("Reason (min 3 chars) is required"); return;
    }
    setEditSaving(true);
    try {
      await api.post("/inventory/manual-adjust", {
        distributor_id: editRow.distributor_id, sku_id: editRow.sku_id,
        delta_kg: deltaKg, notes: editNotes.trim(),
      });
      toast.success(`${editRow.sku_code} updated (${deltaKg > 0 ? "+" : ""}${deltaKg} kg)`);
      setEditRow(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Adjustment failed"); }
    finally { setEditSaving(false); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="inventory-page">
      <PageHeader
        breadcrumbs={[{ label: "Reports", to: "/" }, { label: "Inventory" }]}
        title="Inventory"
        subtitle="Distributor on-hand SKU stock with ageing and reorder flags."
        favorite={{ key: "reports.inventory", type: "screen", label: "Inventory", route: "/admin/inventory" }}
        testid="inventorypage-page-header"
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Package className="h-6 w-6 text-[#6D2C91]" />
            Distributor Inventory
          </h1>
          <p className="text-sm text-neutral-500">
            Single-source-of-truth on-hand stock across all distributors.
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" disabled={busy}
                data-testid="inv-refresh">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {/* Bulk actions — import stock receipts, and bulk activate/deactivate
          SKUs for a distributor by sheet (DISTRIBUTOR_CODE, SKU_CODE,
          IS_ACTIVE) instead of the checkbox flow below, for a list too big
          to select on screen. */}
      <div className="flex items-center gap-2 flex-wrap">
        <BulkActionsBar
          masterKey="stock_receipts"
          label="Stock Receipts"
          onImportDone={() => load()}
          canExport={false}
        />
        <BulkActionsBar
          masterKey="inventory_status"
          label="SKU Status"
          onImportDone={() => load()}
        />
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="inv-tiles">
        <Tile label="Saleable" value={Number(summary?.saleable_kg ?? 0).toFixed(2)} suffix="kg" tone="emerald" />
        <Tile label="Non-Saleable" value={Number(summary?.non_saleable_kg ?? 0).toFixed(2)} suffix="kg" tone="amber" />
        <Tile label="In-Transit" value={Number(summary?.in_transit_kg ?? 0).toFixed(2)} suffix="kg" tone="sky" />
        <Tile label="Total Kg" value={Number(summary?.total_kg ?? 0).toFixed(2)} suffix="kg" tone="violet" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            placeholder="Search SKU / distributor code / name…"
            className="pl-8 h-9"
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="inv-search"
          />
        </div>
        <div className="flex gap-1.5">
          {["all", "active", "inactive"].map((s) => (
            <button key={s} type="button" onClick={() => setStatus(s)}
                    data-testid={`inv-status-${s}`}
                    className={
                      "rounded-full border px-3 py-1 text-xs font-medium " +
                      (status === s
                        ? "bg-[#6D2C91] border-[#6D2C91] text-white"
                        : "border-neutral-200 text-neutral-700 hover:border-[#6D2C91]")
                    }>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <CommonFilterBar value={filters} onChange={setFilters}
                       showZone showSubZone showDistributor showSku />

      {/* Bulk activate/deactivate for the currently-selected (visible) rows —
          the checkbox counterpart to the "SKU Status" Excel import above,
          for a quick handful of rows already filtered into view. */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-[#6D2C91]/30 bg-[#F3E7FA] px-3 py-2"
             data-testid="inv-bulk-select-bar">
          <span className="text-xs font-medium text-[#6D2C91]">
            {selectedIds.size} selected
          </span>
          <Button size="sm" variant="outline" disabled={bulkToggling}
                  onClick={() => bulkToggle(true)}
                  data-testid="inv-bulk-activate">
            {bulkToggling && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Activate
          </Button>
          <Button size="sm" variant="outline" disabled={bulkToggling}
                  onClick={() => bulkToggle(false)}
                  data-testid="inv-bulk-deactivate">
            {bulkToggling && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Deactivate
          </Button>
          <Button size="sm" variant="ghost" disabled={bulkToggling}
                  onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </div>
      )}

      {/* SKU table */}
      <div className="rounded-xl border border-neutral-200 overflow-x-auto">
        <Table data-testid="inv-table">
          <TableHeader>
            <TableRow className="bg-neutral-50">
              <TableHead className="w-8">
                <Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAll}
                          disabled={items.length === 0}
                          data-testid="inv-select-all" aria-label="Select all visible rows" />
              </TableHead>
              <TableHead>Distributor</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead className="text-right">MRP</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">On-hand (Kg)</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.id} data-testid={`inv-row-${r.sku_code}`}>
                <TableCell>
                  <Checkbox checked={selectedIds.has(r.id)} onCheckedChange={() => toggleSelected(r.id)}
                            data-testid={`inv-select-${r.sku_code}`} aria-label={`Select ${r.sku_code}`} />
                </TableCell>
                <TableCell className="text-xs">
                  <div className="font-medium">{r.distributor_code}</div>
                  <div className="text-neutral-500">{r.distributor_name}</div>
                </TableCell>
                <TableCell className="text-xs">
                  <div className="font-medium flex items-center gap-1">
                    {r.sku_code}
                    {r.fulfilled_beyond_recorded_stock && (
                      <span title="At least one historical line fulfilled beyond recorded stock"
                            data-testid={`inv-flag-${r.sku_code}`}>
                        <Badge className="bg-red-100 text-red-700 hover:bg-red-100 gap-1 text-[10px]">
                          <AlertCircle className="h-3 w-3" /> flagged
                        </Badge>
                      </span>
                    )}
                  </div>
                  <div className="text-neutral-500">{r.sku_name}</div>
                </TableCell>
                <TableCell className="text-right text-xs">₹{r.mrp ?? "—"}</TableCell>
                <TableCell className="text-right text-xs">₹{r.price ?? "—"}</TableCell>
                <TableCell className="text-right text-xs">{r.gst_rate ?? "—"}%</TableCell>
                <TableCell className="text-right text-xs font-mono font-semibold">
                  {Number(r.on_hand_kg || 0).toFixed(2)}
                </TableCell>
                <TableCell className="text-right text-xs font-medium">
                  {(r.value_inr || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}
                </TableCell>
                <TableCell>
                  <Switch checked={r.is_active} onCheckedChange={() => toggleActive(r)}
                          data-testid={`inv-toggle-${r.sku_code}`} />
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)}
                          data-testid={`inv-edit-${r.sku_code}`}
                          className="text-neutral-600">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openMovements(r)}
                          data-testid={`inv-movements-${r.sku_code}`}
                          className="text-[#6D2C91]">
                    Movements
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-sm text-neutral-400 py-8">
                  No stock rows match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <div className="text-[11px] text-neutral-500 p-2 border-t flex items-center justify-between gap-2"
             data-testid="inv-pager">
          <span>
            {total === 0 ? "No rows." : `Showing ${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + items.length} of ${total}.`}
            {" "}Total value tile above.
          </span>
          <span className="flex items-center gap-2">
            <button type="button" disabled={page <= 1 || busy} onClick={() => setPage((p) => p - 1)}
                    className="px-2 py-1 rounded border border-neutral-200 disabled:opacity-40"
                    data-testid="inv-prev">Prev</button>
            <span>Page {page} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
            <button type="button" disabled={page >= Math.ceil(total / PAGE_SIZE) || busy} onClick={() => setPage((p) => p + 1)}
                    className="px-2 py-1 rounded border border-neutral-200 disabled:opacity-40"
                    data-testid="inv-next">Next</button>
          </span>
        </div>
      </div>

      {/* Movements drawer */}
      <Sheet open={!!movementsFor} onOpenChange={(v) => !v && setMovementsFor(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto"
                       data-testid="inv-movements-panel">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Boxes className="h-5 w-5 text-[#6D2C91]" />
              Stock movements
            </SheetTitle>
            <SheetDescription>
              {movementsFor?.sku_code} at {movementsFor?.distributor_code}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-3 space-y-2">
            {movements.length === 0 && (
              <div className="text-center text-neutral-400 text-xs py-6">
                No movements yet.
              </div>
            )}
            {movements.map((m) => (
              <div key={m.id}
                   data-testid={`inv-movement-${m.id}`}
                   className={
                     "rounded-lg border p-2.5 text-xs " +
                     (m.delta_pcs > 0
                       ? "border-emerald-200 bg-emerald-50/60"
                       : "border-red-200 bg-red-50/60")
                   }>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    {m.delta_pcs > 0
                      ? <ArrowUp className="h-3.5 w-3.5 text-emerald-600" />
                      : <ArrowDown className="h-3.5 w-3.5 text-red-600" />}
                    <span className={m.delta_pcs > 0 ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>
                      {m.delta_pcs > 0 ? "+" : ""}{Number(m.delta_kg ?? 0).toFixed(2)} kg
                    </span>
                    <span className="text-neutral-500">
                      ({Number(m.before_kg ?? 0).toFixed(2)} → {Number(m.after_kg ?? 0).toFixed(2)} kg)
                    </span>
                  </div>
                  <span className="text-[10px] text-neutral-400 font-mono">
                    {(m.ts || "").slice(0, 19).replace("T", " ")}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-[11px]">
                  <Badge className="bg-neutral-100 text-neutral-700 hover:bg-neutral-100 text-[10px]">
                    {m.source}
                  </Badge>
                  {m.ref_id && (
                    <span className="text-neutral-500 font-mono truncate max-w-[10rem]">
                      ref: {m.ref_id.slice(0, 24)}{m.ref_id.length > 24 ? "…" : ""}
                    </span>
                  )}
                  {m.went_negative && (
                    <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px] gap-1">
                      <TrendingDown className="h-3 w-3" /> went negative
                    </Badge>
                  )}
                </div>
                {m.notes && <div className="text-[10px] text-neutral-500 italic mt-0.5">{m.notes}</div>}
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Edit on-hand qty — posts a delta to /inventory/manual-adjust so the
          stock_movement audit log stays accurate (same mechanism the
          existing manual-adjust endpoint already uses). */}
      <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
        <DialogContent className="max-w-sm" data-testid="inv-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit stock — {editRow?.sku_code}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-neutral-500">
              {editRow?.distributor_code} · {editRow?.distributor_name}
            </div>
            <div>
              <Label className="text-[11px]">New on-hand qty (kg)</Label>
              <Input type="number" min="0" step="0.01" value={editQty}
                     onChange={(e) => setEditQty(e.target.value)}
                     data-testid="inv-edit-qty" />
              <div className="mt-1 text-[11px] text-neutral-400">
                Current: {Number(editRow?.on_hand_kg ?? 0).toFixed(2)} kg
              </div>
            </div>
            <div>
              <Label className="text-[11px]">Reason (required)</Label>
              <Textarea rows={2} value={editNotes} onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Why is this being adjusted?"
                        data-testid="inv-edit-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editSaving}
                    className="tulsi-gradient text-white"
                    data-testid="inv-edit-save">
              {editSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function Tile({ label, value, suffix, tone }) {
  const toneClass = {
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-800",
    amber:   "border-amber-200 bg-amber-50/60 text-amber-800",
    sky:     "border-sky-200 bg-sky-50/60 text-sky-800",
    violet:  "border-[#6D2C91]/30 bg-[#F3E7FA] text-[#6D2C91]",
  }[tone] || "border-neutral-200 bg-white text-neutral-800";
  return (
    <div className={"rounded-xl border p-3 " + toneClass}
         data-testid={`inv-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[11px] uppercase tracking-widest font-semibold opacity-70">{label}</div>
      <div className="mt-1 text-2xl font-semibold">
        {typeof value === "number" ? value.toLocaleString("en-IN") : value}
        {suffix && <span className="text-xs font-normal opacity-70 ml-1">{suffix}</span>}
      </div>
    </div>
  );
}
